import Papa from "papaparse"
import {
  QuizMetaType,
  QuizResultType,
  QuizAnswerType,
  QuizQuestionType,
  QuizType,
} from "@/types/quiz"

class MalformedCSVError extends Error {
  constructor(message: string) {
    super(message)
    this.name = "MalformedCSVError"
  }
}

type SectionMap = Record<string, string[][]>

const REQUIRED_META_FIELDS = [
  "name",
  "date",
  "author",
  "authorSlug",
  "quizType",
] as const

const REQUIRED_RESULT_FIELDS = [
  "classification",
  "title",
  "description",
] as const

const REQUIRED_QUESTION_BASE_FIELDS = [
  "question",
] as const

function normalizeCell(value: unknown): string {
  return typeof value === "string" ? value.trim() : ""
}

function parseCSV(csvText: string): string[][] {
  const result = Papa.parse<string[]>(csvText, {
    skipEmptyLines: false,
  })

  if (result.errors.length > 0) {
    throw new MalformedCSVError(`CSV parse error: ${result.errors[0].message}`)
  }

  return result.data.map((row) => row.map(normalizeCell))
}

function isBlankRow(row: string[]): boolean {
  return row.every((cell) => cell === "")
}

function groupSections(rows: string[][]): SectionMap {
  const sections: SectionMap = {}
  let currentSection: string | null = null

  for (const row of rows) {
    if (isBlankRow(row)) continue

    if (row[0] === "__SECTION__") {
      const sectionName = row[1]
      if (!sectionName) {
        throw new MalformedCSVError(`Section marker is missing a section name`)
      }
      if (sections[sectionName]) {
        throw new MalformedCSVError(`Duplicate section "${sectionName}"`)
      }
      sections[sectionName] = []
      currentSection = sectionName
      continue
    }

    if (!currentSection) {
      throw new MalformedCSVError(
        `Found content before first section marker: ${JSON.stringify(row)}`
      )
    }

    sections[currentSection].push(row)
  }

  return sections
}

function getSectionRows(sections: SectionMap, sectionName: string): string[][] {
  const rows = sections[sectionName]
  if (!rows || rows.length === 0) {
    throw new MalformedCSVError(`Missing or empty section "${sectionName}"`)
  }
  return rows
}

function parseMetaSection(rows: string[][]): QuizMetaType {
  const meta: Record<string, string> = {}

  for (const row of rows) {
    if (isBlankRow(row)) continue
    const key = row[0]
    const value = row[1] ?? ""

    if (!key) {
      throw new MalformedCSVError(`META section contains a row with no key`)
    }

    meta[key] = value
  }

  for (const field of REQUIRED_META_FIELDS) {
    if (!meta[field]) {
      throw new MalformedCSVError(`META section missing required field "${field}"`)
    }
  }

  return {
    name: meta.name,
    date: meta.date,
    author: meta.author,
    authorSlug: meta.authorSlug,
    quizType: meta.quizType,
    imageUrl: meta.imageUrl || "",
    imageAttribution: meta.imageAttribution || "",
    description: meta.description || "",
    shortDescription: meta.shortDescription || "",
  }
}

function parseHeaderSection(rows: string[][], sectionName: string) {
  const nonBlankRows = rows.filter((row) => !isBlankRow(row))
  if (nonBlankRows.length < 2) {
    throw new MalformedCSVError(
      `${sectionName} section must include a header row and at least one data row`
    )
  }

  const [header, ...dataRows] = nonBlankRows
  return { header, dataRows }
}

function headerIndexMap(header: string[]): Record<string, number> {
  const map: Record<string, number> = {}

  for (let i = 0; i < header.length; i++) {
    const col = header[i]
    if (!col) continue
    if (map[col] !== undefined) {
      throw new MalformedCSVError(`Duplicate column "${col}"`)
    }
    map[col] = i
  }

  return map
}

function getRequiredColumn(
  indexMap: Record<string, number>,
  columnName: string,
  sectionName: string
): number {
  const index = indexMap[columnName]
  if (index === undefined) {
    throw new MalformedCSVError(
      `${sectionName} section missing required column "${columnName}"`
    )
  }
  return index
}

function parseResultsSection(rows: string[][]): QuizResultType[] {
  const { header, dataRows } = parseHeaderSection(rows, "RESULTS")
  const indexMap = headerIndexMap(header)

  for (const field of REQUIRED_RESULT_FIELDS) {
    getRequiredColumn(indexMap, field, "RESULTS")
  }

  return dataRows.map((row, rowIdx) => {
    const result: Record<string, string> = {}

    for (let i = 0; i < header.length; i++) {
      const key = header[i]
      if (!key) continue
      result[key] = row[i] || ""
    }

    for (const field of REQUIRED_RESULT_FIELDS) {
      if (!result[field]) {
        throw new MalformedCSVError(
          `RESULTS row ${rowIdx + 2} missing required value for "${field}"`
        )
      }
    }

    return result as QuizResultType
  })
}

function parseClassificationList(
  raw: string,
  questionText: string,
  answerText: string
): string[] {
  const classifications = raw
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)

  if (classifications.length === 0) {
    throw new MalformedCSVError(
      `Question "${questionText}" answer "${answerText}" has no valid classifications`
    )
  }

  return classifications
}

function findAnswerColumnPairs(header: string[]) {
  const indexMap = headerIndexMap(header)

  const optionColumns = header
    .filter((col) => /^option\d+$/.test(col))
    .map((col) => ({
      name: col,
      n: Number(col.replace("option", "")),
    }))
    .sort((a, b) => a.n - b.n)

  if (optionColumns.length === 0) {
    throw new MalformedCSVError(`QUESTIONS section has no option columns`)
  }

  return optionColumns.map(({ name, n }) => {
    const classificationColumn = `classification${n}`
    const optionIndex = indexMap[name]
    const classificationIndex = indexMap[classificationColumn]

    if (classificationIndex === undefined) {
      throw new MalformedCSVError(
        `QUESTIONS section has "${name}" but is missing "${classificationColumn}"`
      )
    }

    return {
      optionIndex,
      classificationIndex,
    }
  })
}

function parseQuestionsSection(rows: string[][]): QuizQuestionType[] {
  const { header, dataRows } = parseHeaderSection(rows, "QUESTIONS")
  const indexMap = headerIndexMap(header)

  for (const field of REQUIRED_QUESTION_BASE_FIELDS) {
    getRequiredColumn(indexMap, field, "QUESTIONS")
  }

  const answerPairs = findAnswerColumnPairs(header)

  return dataRows.map((row, rowIdx) => {
    const questionText = row[indexMap.question] || ""
    if (!questionText) {
      throw new MalformedCSVError(
        `QUESTIONS row ${rowIdx + 2} missing required value for "question"`
      )
    }

    const question: Record<string, string | QuizAnswerType[]> = {}

    for (let i = 0; i < header.length; i++) {
      const key = header[i]
      if (!key) continue
      if (/^option\d+$/.test(key)) continue
      if (/^classification\d+$/.test(key)) continue

      question[key] = row[i] || ""
    }

    const answers: QuizAnswerType[] = []

    for (const pair of answerPairs) {
      const answerText = row[pair.optionIndex] || ""
      const classificationRaw = row[pair.classificationIndex] || ""

      if (!answerText && !classificationRaw) continue

      if (!answerText || !classificationRaw) {
        throw new MalformedCSVError(
          `Question "${questionText}" has incomplete answer data`
        )
      }

      answers.push({
        text: answerText,
        classifications: parseClassificationList(
          classificationRaw,
          questionText,
          answerText
        ),
      })
    }

    if (answers.length === 0) {
      throw new MalformedCSVError(
        `Question "${questionText}" must have at least one answer`
      )
    }

    question.answers = answers
    return question as QuizQuestionType
  })
}

function validateClassifications(quiz: QuizType) {
  const valid = new Set(quiz.results.map((r) => r.classification))

  for (const question of quiz.questions) {
    for (const answer of question.answers) {
      for (const classification of answer.classifications) {
        if (!valid.has(classification)) {
          throw new MalformedCSVError(
            `Question "${question.question}" answer "${answer.text}" references unknown classification "${classification}"`
          )
        }
      }
    }
  }
}

export function parseQuizCSV(csvText: string): QuizType {
  const rows = parseCSV(csvText)
  const sections = groupSections(rows)

  const meta = parseMetaSection(getSectionRows(sections, "META"))
  const results = parseResultsSection(getSectionRows(sections, "RESULTS"))
  const questions = parseQuestionsSection(getSectionRows(sections, "QUESTIONS"))

  const quiz: QuizType = {
    ...meta,
    results,
    questions,
  }

  validateClassifications(quiz)

  return quiz
}